import type { ExerciseId } from './exercise'
import type { IntervalsPhase, Phase, Workout } from './workout'

/** Un tramo de una fase de intervalos, ya desplegado en el tiempo. */
export type IntervalStep =
  | {
      readonly kind: 'work'
      readonly exerciseId: ExerciseId
      readonly seconds: number
      readonly round: number
      /** Posición del ejercicio dentro del patrón (base 0). */
      readonly indexInRound: number
    }
  | {
      readonly kind: 'rest'
      readonly seconds: number
      readonly round: number
      /** Ejercicio que toca al terminar el descanso. */
      readonly nextExerciseId: ExerciseId
    }

/**
 * Despliega rondas y patrón en la lista lineal de tramos.
 * El último tramo siempre es de trabajo: no se descansa al acabar la fase.
 */
export function buildIntervalSteps(phase: IntervalsPhase): IntervalStep[] {
  const steps: IntervalStep[] = []
  const lastIndex = phase.pattern.length - 1

  for (let round = 1; round <= phase.rounds; round++) {
    phase.pattern.forEach((movement, index) => {
      steps.push({
        kind: 'work',
        exerciseId: movement.exerciseId,
        seconds: movement.seconds,
        round,
        indexInRound: index,
      })

      const closesRound = index === lastIndex
      const isVeryLast = closesRound && round === phase.rounds
      if (isVeryLast) return

      const next = closesRound ? phase.pattern[0] : phase.pattern[index + 1]
      if (!next) return

      steps.push({
        kind: 'rest',
        seconds: closesRound ? (phase.roundRestSeconds ?? phase.restSeconds) : phase.restSeconds,
        round,
        nextExerciseId: next.exerciseId,
      })
    })
  }

  return steps
}

/** Dónde estamos dentro de una fase de intervalos, dado el tiempo transcurrido. */
export interface IntervalPosition {
  readonly step: IntervalStep
  readonly stepIndex: number
  /** Segundos que quedan del tramo actual, redondeados hacia arriba. */
  readonly remainingInStep: number
  /** Progreso del tramo actual, de 0 a 1. */
  readonly stepProgress: number
  readonly finished: boolean
}

export function locateInterval(steps: readonly IntervalStep[], elapsedSeconds: number): IntervalPosition {
  let offset = 0

  for (let index = 0; index < steps.length; index++) {
    const step = steps[index]
    if (!step) break
    const end = offset + step.seconds
    if (elapsedSeconds < end) {
      const intoStep = elapsedSeconds - offset
      return {
        step,
        stepIndex: index,
        remainingInStep: Math.ceil(step.seconds - intoStep),
        stepProgress: step.seconds === 0 ? 1 : intoStep / step.seconds,
        finished: false,
      }
    }
    offset = end
  }

  const last = steps[steps.length - 1]
  return {
    step: last as IntervalStep,
    stepIndex: steps.length - 1,
    remainingInStep: 0,
    stepProgress: 1,
    finished: true,
  }
}

/** Duración total de una fase de intervalos, en segundos. */
export function intervalDuration(phase: IntervalsPhase): number {
  return buildIntervalSteps(phase).reduce((total, step) => total + step.seconds, 0)
}

/**
 * Cuánto dura una fase. En AFAP no hay duración fija, así que se usa la
 * estimación del entrenador para calcular el total de la sesión.
 */
export function phaseDuration(phase: Phase): number {
  switch (phase.kind) {
    case 'intervals':
      return intervalDuration(phase)
    case 'amrap':
    case 'countdown':
      return phase.durationSeconds
    case 'afap':
      return phase.estimatedSeconds
  }
}

export function workoutDuration(workout: Workout): number {
  return workout.phases.reduce((total, phase) => total + phaseDuration(phase), 0)
}

/** Segundos acumulados por las fases anteriores a `index`. */
export function durationBefore(workout: Workout, index: number): number {
  return workout.phases.slice(0, index).reduce((total, phase) => total + phaseDuration(phase), 0)
}

/** Los ejercicios que aparecen en una fase, en orden y sin repetir. */
export function exercisesOf(phase: Phase): ExerciseId[] {
  const ids =
    phase.kind === 'intervals'
      ? phase.pattern.map((movement) => movement.exerciseId)
      : phase.kind === 'amrap'
        ? phase.movements.map((movement) => movement.exerciseId)
        : phase.kind === 'afap'
          ? phase.blocks.map((movement) => movement.exerciseId)
          : []

  return [...new Set(ids)]
}
